import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { useEffect } from 'react';

type RichTextEditorProps = {
  value: string;
  onChange: (content: string) => void;
  height?: number;
};

export function RichTextEditor({ value, onChange, height = 500 }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image,
    ],
    content: value,
    onUpdate: ({ editor: editorInstance }) => {
      onChange(editorInstance.getHTML());
    },
    editorProps: {
      attributes: {
        style: `min-height: ${height}px; padding: 1rem; outline: none;`,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-md">
      <div className="border-b p-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700"
        >
          Redo
        </button>
        <div className="border-l mx-1" />
        <select
          onChange={(e) => {
            const selectedValue = e.target.value;
            if (selectedValue === 'paragraph') {
              editor.chain().focus().setParagraph().run();
            } else {
              editor
                .chain()
                .focus()
                .toggleHeading({ level: parseInt(selectedValue) as 1 | 2 | 3 })
                .run();
            }
          }}
          className="px-2 py-1 text-sm border rounded text-gray-700"
        >
          <option value="paragraph">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
        <div className="border-l mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
            editor.isActive('bold') ? 'bg-gray-300 text-gray-900' : 'text-gray-700'
          }`}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
            editor.isActive('italic') ? 'bg-gray-300 text-gray-900' : 'text-gray-700'
          }`}
        >
          <em>I</em>
        </button>
        <div className="border-l mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          Left
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          Center
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          Right
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          Justify
        </button>
        <div className="border-l mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive('bulletList') ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive('orderedList') ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          1. List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().liftListItem('listItem').run()}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700"
        >
          Outdent
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700"
        >
          Indent
        </button>
        <div className="border-l mx-1" />
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700 ${
            editor.isActive('link') ? 'bg-gray-300 text-gray-900' : ''
          }`}
        >
          Link
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter image URL:');
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            }
          }}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700"
        >
          Image
        </button>
        <div className="border-l mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          className="px-2 py-1 text-sm border rounded hover:bg-gray-100 text-gray-700"
        >
          Clear Format
        </button>
      </div>
      <EditorContent editor={editor} />
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: ${height}px;
          padding: 1rem;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
